import { Map as MBMap } from '@/components/Map';
import { useMainContext } from '@/lib/context';
import { YEARS } from '@/lib/fixedTypes';
import { DataLevel, MapSettings, Result } from '@/lib/model';
import center from '@turf/center';
import { AllGeoJSON } from '@turf/helpers';
import { MapLayerMouseEvent } from 'mapbox-gl';
import * as React from 'react';
import { Layer, LayerProps, Source, useMap } from 'react-map-gl';
import './index.scss';
import { Popup } from './Popup';

// Default values
const sourceId = 'aurelianv.Mappa_Reg_LazioLombardia_tileset';
const sourceUrl = 'mapbox://aurelianv.Mappa_Reg_LazioLombardia_tileset';

export const Map: React.FC<MapSettings> = ({
  layerPropId,
  sourceLayer,
  zoom,
}) => {
  const {
    results,
    handleResult,
    dataLevel,
    lastResult,
    setComuneIsLoading,
    selectedCandidate,
    selectedResults,
    selectedYear,
    isTurnoutOpen,
  } = useMainContext();
  const [cursor, setCursor] = React.useState<string>('auto');
  const [point, setPoint] = React.useState({ x: 0, y: 0 });
  const [popupResult, setPopupResult] = React.useState<Result | null>(null);
  const { electionsMap: map } = useMap();
  const [isCoordinateSet, setIsCoordinateSet] = React.useState(false);

  const layersObj = results.reduce(
    (acc, el) => ({
      ...acc,
      [el.mapLayer.layerId]: el,
    }),
    {}
  );

  const interactiveLayerIds = Object.keys(layersObj);

  React.useEffect(() => {
    const { coordinates } = lastResult?.mapLayer;
    const isItSet = !!coordinates.length;

    setIsCoordinateSet(isItSet);
  }, [lastResult]);

  React.useEffect(() => {
    // set map center to given coordinates
    const goTo = ([lng, lat]: number[]) => {
      map?.setCenter({
        lat,
        lng,
      });
      // on Region TAB change
      if (
        (lng === 12.9896 && lat === 41.6552) ||
        (lng === 9.8452 && lat === 45.4791)
      ) {
        map?.setZoom(6); // reset zoom level
      }
    };

    const onLayerLoad = () => {
      const layerId = `${lastResult.mapLayer.layerId}`;

      // check if layer has rendered before proceeding
      const isLayerRendered = map?.getLayer(layerId);
      if (!isLayerRendered) {
        return;
      }

      let layers = map?.queryRenderedFeatures(undefined, {
        layers: [layerId],
      });

      if (layers?.length === 0) {
        // Juan please check this
        let layersAll = map?.querySourceFeatures('composite', {
          sourceLayer: '20230213_Comuni_LazioLombardia',
        });
        layers = layersAll?.filter(l => l.id === +layerId);
      }

      if (!layers?.length) {
        return;
      }

      const { coordinates } = center(layers[0] as AllGeoJSON).geometry;

      // go to coordinate
      goTo(coordinates);

      // setting flag to true after finding coordinate
      setIsCoordinateSet(true);
      setComuneIsLoading(false);
    };

    const { coordinates } = lastResult?.mapLayer;

    if (!coordinates.length) {
      // these following listeners work to understand
      // when map has fully loaded
      if (isCoordinateSet) {
        map?.getMap().off('idle', onLayerLoad);
      } else {
        map?.once('idle', onLayerLoad);
      }

      return;
    }

    setIsCoordinateSet(true);
    goTo(coordinates);
  }, [
    map,
    lastResult,
    isCoordinateSet,
    setIsCoordinateSet,
    setComuneIsLoading,
  ]);

  React.useEffect(() => {
    map?.setZoom(zoom);
  }, [map, zoom]);

  // show popup on mouse move
  const onMouseMove = async (e: MapLayerMouseEvent) => {
    const feature = e.features?.length && e.features[0];
    if (!feature) {
      // close popup
      setPopupResult(null);
      return;
    }

    const layerId = feature.layer.id as keyof typeof layersObj;

    // position popup
    setPoint(e.point);

    const result = (layersObj[layerId] || null) as Result;

    // open popup with info
    setPopupResult(result);
  };

  // return layer when clicked with current coordinates
  const onClick = async (e: MapLayerMouseEvent) => {
    // avoid clicking on layer if in inner selected candidate
    if (selectedCandidate) {
      return;
    }

    const feature = e.features?.length && e.features[0];
    if (!popupResult || !feature) {
      return;
    }

    // zoom and center to new coordinates
    const { coordinates: newCoordinates } = center(
      feature as AllGeoJSON
    ).geometry;

    while (Math.abs(e.lngLat.lng - newCoordinates[0]) > 180) {
      newCoordinates[0] += e.lngLat.lng > newCoordinates[0] ? 360 : -360;
    }

    // add result to left pannel
    handleResult(
      {
        ...popupResult,
        mapLayer: {
          ...popupResult.mapLayer,
          coordinates: newCoordinates,
        },
      },
      false
    );

    // raise "electio:navigate" event for sole
    const selectedYearObj = YEARS.find(y => y.value === selectedYear);
    let nomeProvincia = selectedResults[1]?.placeName || '';
    let nomeComune = selectedResults[2]?.placeName || '';

    if (popupResult.level === DataLevel.PROVINCIA) {
      nomeProvincia = popupResult.placeName;
    } else if (popupResult.level === DataLevel.COMUNE) {
      nomeComune = popupResult.placeName;
    }

    const event = new CustomEvent('electio:navigate', {
      detail: {
        nomeRegione: selectedResults[0]?.placeName || '',
        nomeProvincia,
        nomeComune,
        anno: selectedYearObj?.label,
      },
    });

    document.dispatchEvent(event);
  };

  // set cursor styles
  const onMouseEnter = () => {
    setCursor('pointer');
  };

  const onMouseLeave = () => {
    setCursor('auto');
    // close popup
    setPopupResult(null);
  };

  const areResultsValid = results.length > 0 && results[0].level === dataLevel;

  return (
    <div className="Map">
      <MBMap
        id="electionsMap"
        initialViewState={{
          latitude: lastResult?.mapLayer.coordinates[1],
          longitude: lastResult?.mapLayer.coordinates[0],
          zoom,
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        onClick={onClick}
        interactiveLayerIds={interactiveLayerIds}
        cursor={cursor}
        cooperativeGestures={true}
      >
        <Source id={sourceId} type="vector" url={sourceUrl}>
          {areResultsValid &&
            results.map((result, i) => {
              const mapLayerId = Number(result?.mapLayer?.layerId) ?? null;

              if (mapLayerId === null) {
                return null;
              }

              const isSelected =
                lastResult?.mapLayer.layerId === result.mapLayer.layerId ||
                lastResult?.level === DataLevel.MAIN;

              const opacity =
                !selectedCandidate && !isTurnoutOpen
                  ? isSelected
                    ? 1
                    : 0.6
                  : result.mapLayer.opacity;

              const layerProperties: LayerProps = {
                id: `${result.mapLayer.layerId}`,
                'source-layer': sourceLayer,
                minzoom: 4, // to improve speed loading
                maxzoom: 10, // to improve speed loading
                type: 'fill',
                filter: ['==', layerPropId, mapLayerId],
                paint: {
                  'fill-color': result.mapLayer.fillColor,
                  'fill-opacity': opacity,
                  'fill-outline-color': 'rgba(0, 0, 0, 0.3)',
                },
                beforeId: 'waterway-shadow',
              };

              return (
                <Layer
                  {...layerProperties}
                  key={`${layerPropId}_${result.mapLayer.layerId}_${i}`}
                />
              );
            })}
        </Source>
        {popupResult && (
          <Popup
            data={popupResult}
            x={point.x || 0}
            y={point.y || 0}
            offset={15}
            selectedCandidate={selectedCandidate}
            selectedYear={selectedYear}
          />
        )}
      </MBMap>
    </div>
  );
};
